// src/CourseContent/premium/Plan.jsx

import React, { useEffect, useState } from "react";
import { Container, Col, Row } from "react-bootstrap";
import { Typography, Card, Box, Divider, List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { axiosInstance } from "../../component/Api/instance";
import { showSuccess, showError, showWarning } from "../../utils/popup";
import Dialog from "../../component/ui/Dialog";
import { PrimaryButton, SecondaryButton } from "../../component/ui/Buttons";
import Crown from "../../asserts/crown.png";
import CheckIcon from "@mui/icons-material/Check";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { styled } from "@mui/material/styles";

const PlanPageContainer = styled(Box)(({ theme }) => ({
  minHeight: "85vh",
  padding: "32px 0",
  backgroundColor: theme.palette.background.default,
}));

const PlanCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'isSelected'
})(({ theme, isSelected }) => ({
  borderRadius: "24px",
  padding: "32px",
  backgroundColor: theme.palette.background.paper,
  border: `2px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
  boxShadow: isSelected ? "var(--color-shadow-hover)" : "none",
  textAlign: "center",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  transition: "all 0.2s ease",
  position: "relative",
  overflow: "hidden",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "var(--color-shadow-hover)",
  },
}));

const FeaturedBadge = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "16px",
  right: "-32px",
  background: "linear-gradient(135deg, #D97706 0%, #B45309 100%)",
  color: "#FFFFFF",
  transform: "rotate(45deg)",
  padding: "4px 32px",
  fontSize: "0.675rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
}));

const PriceText = styled(Typography)(({ theme }) => ({
  fontSize: "2.25rem",
  fontWeight: 800,
  color: theme.palette.text.primary,
  margin: "16px 0 8px 0",
  "& span": {
    fontSize: "1rem",
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
}));

const CoursePlan = () => {
  const { sno } = useParams();
  const navigate = useNavigate();
  const emailId = localStorage.getItem("userMail");
  const courseplan = localStorage.getItem("category");
  const planName = localStorage.getItem("plan");

  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState([]);

  const handleClickOpen = (amount, title) => {
    setOpen(true);
    setAmount(amount);
    setTitle(title);
  };

  const handlePay = () => {
    Payment();
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    fetchData();
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailId, sno]);

  const fetchData = async () => {
    try {
      const response = await axiosInstance.post(`get/U_ViewCoursePlans.php`, {
        userId: emailId,
        courseId: sno,
      });
      if (response.status === 200) {
        if (response.data.message === "timeout") {
          navigate("/signin");
          return;
        }
        setPlans(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axiosInstance.post(`get/U_ViewProfile.php`, {
        email: emailId,
      });
      if (response.status === 200) {
        if (response.data.message === "timeout") {
          navigate("/signin");
          return;
        }
        setCategory(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const Payment = async () => {
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        showError("Razorpay SDK failed to load. Are you offline?");
        return;
      }

      const payload = {
        user_id: emailId,
        course_id: sno,
        plan_id: selectedPlan.id,
        amount: selectedPlan.amount,
        account_type: courseplan,
        duration: selectedPlan.duration,
        plan_name: selectedPlan.title,
        payment_for: "course_plan",
      };

      const response = await axiosInstance.post(`post/createOrder.php`, payload);
      const order = response.data;

      if (!order.success || !order.order_id) {
        showError(order.message || "Failed to initiate payment.");
        return;
      }

      const options = {
        key: order.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency || "INR",
        name: "Nursing Update",
        description: `Upgrade to ${selectedPlan.title}`,
        order_id: order.order_id,
        handler: async function (res) {
          try {
            const verifyPayload = {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            };

            const verificationResponse = await axiosInstance.post(
              `post/verifyPayment.php`,
              verifyPayload
            );

            if (verificationResponse.data.success) {
              try {
                const profileRes = await axiosInstance.post(`get/U_ViewProfile.php`, {
                  email: emailId,
                });
                if (profileRes.status === 200 && Array.isArray(profileRes.data) && profileRes.data.length > 0) {
                  const userProfile = profileRes.data[0];
                  if (userProfile.plan_category) {
                    localStorage.setItem("category", userProfile.plan_category);
                  }
                }
              } catch (profileErr) {
                console.error("Error refreshing profile after payment:", profileErr);
              }

              showSuccess("Payment successful! Your premium subscription is now active.");
              window.location.href = '/courses';
            } else {
              showError(
                verificationResponse.data.message ||
                  "Payment verification failed. Please contact support."
              );
            }
          } catch (verifyError) {
            console.error("Verification error:", verifyError);
            if (!verifyError._popupShown) {
              showError("Error verifying payment. Please contact support.");
            }
          }
        },
        prefill: {
          email: emailId,
        },
        theme: {
          color: "#1E3A8A",
        },
        modal: {
          ondismiss: function () {
            console.log("Payment modal dismissed by user.");
          },
        },
      };

      const razorpayObj = new window.Razorpay(options);
      razorpayObj.open();

    } catch (error) {
      console.error("Error processing payment:", error);
      if (!error._popupShown) {
        showError("An error occurred while setting up the payment.");
      }
    }
  };

  const handleViewPlan = (plan) => {
    setSelectedPlan(plan);
  };

  return (
    <PlanPageContainer>
      <Container>
        
        {/* Modernized Page Header */}
        <Box style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px", marginBottom: "32px" }}>
          <img src={Crown} height="28px" alt="" style={{ marginBottom: "6px" }} />
          <Typography variant="h1" style={{ fontWeight: 800, textAlign: "center" }}>
            Premium Course Plans
          </Typography>
        </Box>

        <Row className="g-4 justify-content-center">
          {Array.isArray(plans) &&
            plans.map((item, index) => {
              const isSelected = selectedPlan === item;
              const isSubscribed = item.is_subscribed == 1 || planName === item.title;
              const isGold = item.title.toLowerCase().includes("gold") || item.title.toLowerCase().includes("premium");
              
              return (
                <Col xs={12} sm={6} md={4} lg={3} key={index}>
                  <PlanCard isSelected={isSelected}>
                    {isGold && <FeaturedBadge>Popular</FeaturedBadge>}

                    <Box>
                      {/* Title block */}
                      <Typography 
                        variant="h3" 
                        style={{ 
                          fontWeight: 800, 
                          color: isGold ? "var(--color-secondary)" : "var(--color-primary)",
                          textTransform: "uppercase",
                          letterSpacing: "0.02em",
                          fontSize: "1.125rem",
                          marginBottom: "12px",
                        }}
                      >
                        {item.title}
                      </Typography>

                      <PriceText>
                        ${item.amount}
                        <span> / {item.duration} Mo</span>
                      </PriceText>
                    </Box>

                    {/* Features list */}
                    {isSelected && item.description && (
                      <Box style={{ textAlign: "left", margin: "24px 0", borderTop: "1px solid var(--color-border)", paddingTop: "16px" }}>
                        <List style={{ padding: 0 }}>
                          {item.description.split(",").map((word, idx) => {
                            if (!word.trim()) return null;
                            return (
                              <ListItem key={idx} style={{ padding: "4px 0", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                <ListItemIcon style={{ minWidth: "auto", color: "var(--color-success)", marginTop: "2px" }}>
                                  <CheckIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={word.trim()} 
                                  primaryTypographyProps={{ style: { fontSize: "0.825rem", fontWeight: 550, color: "var(--color-text-secondary)" } }}
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      </Box>
                    )}

                    {/* CTA Actions */}
                    <Box style={{ marginTop: "24px" }}>
                      {!isSelected ? (
                        <SecondaryButton
                          onClick={() => handleViewPlan(item)}
                          style={{ width: "100%", height: "42px", borderRadius: "12px" }}
                        >
                          View Plan
                        </SecondaryButton>
                      ) : (
                        <PrimaryButton
                          disabled={isSubscribed}
                          onClick={() => handleClickOpen(item.amount, item.title)}
                          style={{
                            width: "100%",
                            height: "44px",
                            borderRadius: "12px",
                            background: isSubscribed ? "var(--color-border)" : "linear-gradient(135deg, #1E3A8A 0%, #0D1B2A 100%)",
                            boxShadow: isSubscribed ? "none" : "0 4px 12px rgba(30, 58, 138, 0.15)",
                          }}
                        >
                          {isSubscribed ? "Active Plan" : "Buy Now"}
                        </PrimaryButton>
                      )}
                    </Box>

                  </PlanCard>
                </Col>
              );
            })}
        </Row>
      </Container>

      {/* CONFIRMATION PURCHASE DIALOG */}
      <Dialog
        open={open}
        onClose={handleClose}
        title="Confirm Course Membership Upgrade"
      >
        <Typography variant="body1" color="textSecondary" style={{ lineHeight: 1.6, marginBottom: "20px" }}>
          You have selected the **{title}** membership plan. This will unlock premium licensing test prep questions and subject exams for this course.
        </Typography>
        <Divider style={{ margin: "16px 0" }} />
        <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
          <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
            Total Charges:
          </Typography>
          <Typography variant="h2" style={{ fontWeight: 850, color: "var(--color-primary)" }}>
            ${amount}
          </Typography>
        </Box>
        <Box style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
          <SecondaryButton onClick={handleClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton 
            onClick={handlePay}
            style={{
              background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
              boxShadow: "0 4px 12px rgba(5, 150, 105, 0.2)",
            }}
          >
            Confirm & Pay
          </PrimaryButton>
        </Box>
      </Dialog>
    </PlanPageContainer>
  );
};

export default CoursePlan;
